import { supabase } from '@/integrations/supabase/client';
import { setCurrentSupabaseOrganization } from '@/lib/supabaseOrganization';
import type { Json } from '@/integrations/supabase/types';
import type {
  ContentSubmission,
  ContentSubmissionUpdate,
  CreateSubmissionDto,
  UpdateSubmissionDto,
  TopPost,
} from '../types/content.types';
import { mapDbRowToContentSubmission } from '../services/content.mappers';
import { isValidTransition, STATUS_LABELS } from '../services/contentStatus.service';

interface RpcTopPostRow {
  id: string;
  post_url: string | null;
  content_type: string;
  platform: string;
  influencer_name: string;
  influencer_avatar: string | null;
  thumbnail_url: string | null;
  views: number;
  reach: number;
  likes: number;
  comments: number;
  shares: number;
  engagement: number;
  engagement_rate: number;
  er_followers?: number | null;
  er_views?: number | null;
  published_on: string | null;
}

export class ContentSubmissionRepository {
  /**
   * Fetch all posts for an influencer in a campaign with tenant organization verification.
   */
  async getPostsByCampaignInfluencerId(
    orgId: string,
    campaignInfluencerId: string
  ): Promise<ContentSubmission[]> {
    if (!campaignInfluencerId) return [];

    await setCurrentSupabaseOrganization(orgId);

    const { data, error } = await supabase
      .from('content_submissions')
      .select('*')
      .eq('campaign_influencer_id', campaignInfluencerId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapDbRowToContentSubmission);
  }

  /**
   * Fetch all content submissions across all influencers for a given campaign.
   */
  async getPostsByCampaignId(
    orgId: string,
    campaignId: string
  ): Promise<ContentSubmission[]> {
    if (!campaignId || !orgId) return [];

    await setCurrentSupabaseOrganization(orgId);

    // 1. Fetch all influencer records belonging to this campaign
    const { data: campaignInfluencers, error: ciError } = await supabase
      .from('campaign_influencers')
      .select('id')
      .eq('campaign_id', campaignId);

    if (ciError) {
      console.error('Error fetching campaign influencers for deliverables scrape:', ciError);
      throw ciError;
    }

    const ciIds = (campaignInfluencers || []).map((ci) => ci.id);
    if (ciIds.length === 0) return [];

    // 2. Fetch all content submissions belonging to those campaign influencers
    const { data, error } = await supabase
      .from('content_submissions')
      .select('*')
      .in('campaign_influencer_id', ciIds)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching content submissions by campaign influencer IDs:', error);
      throw error;
    }

    return (data || []).map(mapDbRowToContentSubmission);
  }

  /**
   * Create a new content submission.
   */
  async createSubmission(
    orgId: string,
    data: CreateSubmissionDto
  ): Promise<ContentSubmission> {
    await setCurrentSupabaseOrganization(orgId);

    const payload: ContentSubmissionInsert = {
      campaign_influencer_id: data.campaign_influencer_id,
      platform: data.platform,
      type: data.type,
      post_url: data.post_url || null,
      status: 'draft',
      performance_metrics: (data.performance_metrics
        ? JSON.parse(JSON.stringify(data.performance_metrics))
        : null) as Json,
    };

    const { data: result, error } = await supabase
      .from('content_submissions')
      .insert(payload)
      .select()
      .single();

    if (error) throw error;
    return mapDbRowToContentSubmission(result);
  }

  /**
   * Bulk insert content submissions.
   */
  async createSubmissionsBulk(
    orgId: string,
    submissions: CreateSubmissionDto[]
  ): Promise<ContentSubmission[]> {
    if (submissions.length === 0) return [];

    await setCurrentSupabaseOrganization(orgId);

    const rows: ContentSubmissionInsert[] = submissions.map((data) => ({
      campaign_influencer_id: data.campaign_influencer_id,
      platform: data.platform,
      type: data.type,
      post_url: data.post_url || null,
      status: 'draft',
      performance_metrics: (data.performance_metrics
        ? JSON.parse(JSON.stringify(data.performance_metrics))
        : null) as Json,
    }));

    const { data: result, error } = await supabase
      .from('content_submissions')
      .insert(rows)
      .select();

    if (error) throw error;
    return (result || []).map(mapDbRowToContentSubmission);
  }

  /**
   * Update a content submission with status transition validation.
   */
  async updateSubmission(
    orgId: string,
    params: UpdateSubmissionDto
  ): Promise<ContentSubmission> {
    const { id, currentStatus, status, ...rest } = params;

    if (status && status !== currentStatus) {
      if (!isValidTransition(currentStatus, status)) {
        throw new Error(
          `Invalid status transition: ${STATUS_LABELS[currentStatus]} → ${STATUS_LABELS[status]}`
        );
      }
    }

    await setCurrentSupabaseOrganization(orgId);

    const updatePayload: ContentSubmissionUpdate = {};
    if (rest.platform !== undefined) updatePayload.platform = rest.platform;
    if (rest.type !== undefined) updatePayload.type = rest.type;
    if (rest.post_url !== undefined) updatePayload.post_url = rest.post_url;
    if (status !== undefined) updatePayload.status = status;
    if (rest.rejection_reason !== undefined) updatePayload.rejection_reason = rest.rejection_reason;
    if (rest.performance_metrics !== undefined) {
      updatePayload.performance_metrics = JSON.parse(
        JSON.stringify(rest.performance_metrics)
      ) as Json;
    }

    const { data: result, error } = await supabase
      .from('content_submissions')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.code === '23514' && error.message.includes('Invalid status transition')) {
        throw new Error(error.message);
      }
      throw error;
    }

    return mapDbRowToContentSubmission(result);
  }

  /**
   * Delete a content submission by ID.
   */
  async deleteSubmission(
    orgId: string,
    id: string
  ): Promise<{ id: string }> {
    await setCurrentSupabaseOrganization(orgId);

    const { error } = await supabase
      .from('content_submissions')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { id };
  }

  /**
   * Fetch top performing posts for a campaign.
   */
  async getTopPerformingPosts(
    orgId: string,
    campaignId: string
  ): Promise<TopPost[]> {
    await setCurrentSupabaseOrganization(orgId);

    const { data, error } = await supabase.rpc('get_top_performing_posts', {
      p_campaign_id: campaignId,
    });

    if (error) throw error;
    const rows = (data ?? []) as unknown as RpcTopPostRow[];

    // If er_followers was not returned by RPC (e.g. legacy RPC), fetch from content_submissions
    let erMap: Record<string, { erFollowers: number | null; erViews: number | null }> = {};
    if (rows.length > 0 && rows[0].er_followers === undefined) {
      const postIds = rows.map((r) => r.id);
      const { data: subs } = await supabase
        .from('content_submissions')
        .select('id, er_followers, er_views')
        .in('id', postIds);
      if (subs) {
        erMap = Object.fromEntries(
          subs.map((s) => [s.id, { erFollowers: s.er_followers, erViews: s.er_views }])
        );
      }
    }

    return rows.map((row) => ({
      id: row.id,
      postUrl: row.post_url,
      contentType: row.content_type,
      platform: row.platform,
      influencerName: row.influencer_name,
      influencerAvatar: row.influencer_avatar,
      thumbnailUrl: row.thumbnail_url,
      views: row.views,
      reach: row.reach,
      likes: row.likes,
      comments: row.comments,
      shares: row.shares,
      engagement: row.engagement,
      engagementRate: row.engagement_rate,
      erFollowers: row.er_followers ?? erMap[row.id]?.erFollowers ?? null,
      erViews: row.er_views ?? erMap[row.id]?.erViews ?? null,
      publishedOn: row.published_on,
    }));
  }
}

export const contentSubmissionRepository = new ContentSubmissionRepository();
